import React from "react";
import { Nav, NavItem, NavLink } from "reactstrap";
import { MOVEMENT_DATE_METRIC_OPTIONS } from "../constants";
import {
  formatCurrency,
  formatNumber,
  formatShortDayMonth,
  truncateWithDots,
} from "../formatters";
import type { MovementByDateRow, MovementDateMetric } from "../types";

type MovementByDateTabProps = {
  movementDateColumns: string[];
  movementRowsByDate: MovementByDateRow[];
  movementDateAvailability: Record<MovementDateMetric, boolean>;
  movementDateMetric: MovementDateMetric;
  onMovementDateMetricChange: (value: MovementDateMetric) => void;
  metricMode: "cantidad" | "monto" | "ambos";
};

const HEADER_CELL_STYLE: React.CSSProperties = {
  fontSize: "0.925rem",
  lineHeight: 1.05,
  padding: "0.2rem 0.28rem",
  whiteSpace: "nowrap",
};

const BODY_CELL_STYLE: React.CSSProperties = {
  fontSize: "0.925rem",
  lineHeight: 1.06,
  padding: "0.16rem 0.28rem",
};

const ARTICLE_CELL_STYLE: React.CSSProperties = {
  ...BODY_CELL_STYLE,
  minWidth: 260,
};

const MovementByDateTab = ({
  movementDateColumns,
  movementRowsByDate,
  movementDateAvailability,
  movementDateMetric,
  onMovementDateMetricChange,
  metricMode,
}: MovementByDateTabProps) => (
  <>
    <div className="mb-3">
      <Nav pills className="gap-2">
        {MOVEMENT_DATE_METRIC_OPTIONS.map((option) => (
          <NavItem key={option.value}>
            {(() => {
              const isAvailable = movementDateAvailability[option.value];
              const isActive = movementDateMetric === option.value;
              const isActuallyActive = isAvailable && isActive;
              return (
                <NavLink
                  href="#"
                  className={`${isActuallyActive ? "active" : ""} ${
                    !isAvailable ? "disabled text-muted opacity-50" : ""
                  }`}
                  style={!isAvailable ? { pointerEvents: "none" } : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    if (!isAvailable) return;
                    onMovementDateMetricChange(option.value);
                  }}
                >
                  {option.label}
                </NavLink>
              );
            })()}
          </NavItem>
        ))}
      </Nav>
    </div>
    <div className="table-responsive">
      <table className="table align-middle table-striped mb-0 control-stock-sticky-table">
        <thead className="table-light">
          <tr>
            <th style={HEADER_CELL_STYLE}>ARTICULO</th>
            {movementDateColumns.map((date, index) => (
              <th
                key={date}
                className="text-end"
                style={{
                  ...HEADER_CELL_STYLE,
                  borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
                  backgroundColor:
                    index % 2 === 0 ? undefined : "rgba(255, 255, 255, 0.03)",
                }}
              >
                {formatShortDayMonth(date)}
              </th>
            ))}
            <th
              className="text-end"
              style={{
                ...HEADER_CELL_STYLE,
                borderLeft: "2px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              TOTAL
            </th>
          </tr>
        </thead>
        <tbody>
          {movementRowsByDate.length === 0 ? (
            <tr>
              <td
                colSpan={movementDateColumns.length + 2}
                className="text-center text-muted py-4"
                style={BODY_CELL_STYLE}
              >
                No hay movimientos para los filtros seleccionados.
              </td>
            </tr>
          ) : (
            movementRowsByDate.map((row) => (
              <tr key={`${row.idArticulo}-${row.articulo}`}>
                <td style={ARTICLE_CELL_STYLE}>
                  <div className="d-inline-flex align-items-center gap-1 text-nowrap">
                    {row.idArticulo ? <span className="badge bg-primary">{row.idArticulo}</span> : null}
                    <span className="fw-semibold" title={row.articulo || row.idArticulo || "Sin artículo"}>
                      {truncateWithDots(row.articulo || row.idArticulo || "Sin artículo", 28)}
                    </span>
                  </div>
                </td>
                {movementDateColumns.map((date, index) => {
                  const current = row.porFecha[date];
                  const qty = Number(current?.cantidad ?? 0);
                  const amount = Number(current?.monto ?? 0);
                  const sharedStyle = {
                    ...BODY_CELL_STYLE,
                    borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
                    backgroundColor:
                      index % 2 === 0 ? undefined : "rgba(255, 255, 255, 0.02)",
                  } as React.CSSProperties;
                  if (metricMode === "cantidad") {
                    return (
                      <td key={date} className="text-end" style={sharedStyle}>
                        {qty === 0 ? "—" : formatNumber(qty)}
                      </td>
                    );
                  }
                  if (metricMode === "monto") {
                    return (
                      <td key={date} className="text-end" style={sharedStyle}>
                        {amount === 0 ? "—" : formatCurrency(amount)}
                      </td>
                    );
                  }
                  return (
                    <td key={date} className="text-end lh-sm" style={sharedStyle}>
                      <div>{qty === 0 ? "—" : formatNumber(qty)}</div>
                      <small className="text-muted">
                        {amount === 0 ? "—" : formatCurrency(amount)}
                      </small>
                    </td>
                  );
                })}
                <td
                  className="text-end fw-semibold lh-sm"
                  style={{
                    ...BODY_CELL_STYLE,
                    borderLeft: "2px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  {metricMode === "cantidad" ? (
                    row.totalCantidad === 0 ? "—" : formatNumber(row.totalCantidad)
                  ) : metricMode === "monto" ? (
                    row.totalMonto === 0 ? "—" : formatCurrency(row.totalMonto)
                  ) : (
                    <>
                      <div>{row.totalCantidad === 0 ? "—" : formatNumber(row.totalCantidad)}</div>
                      <small className="text-muted">
                        {row.totalMonto === 0 ? "—" : formatCurrency(row.totalMonto)}
                      </small>
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </>
);

export default MovementByDateTab;
